/**
 * Display formatting for the values core sends.
 *
 * Two rules hold this together, and both are canon:
 *
 *   - money is integer cents (`docs/ARCHITECTURE.md` § API style) and is divided in exactly
 *     one place — `formatMoney` — so a balance cannot be rounded one way on the desk and
 *     another way on the board;
 *   - Dealbid's day is a **UTC** day (canon § 8), so a timestamp is rendered in UTC. A
 *     seller west of Greenwich must not read "approved 23 August" for a row core stamped
 *     on the 24th, because every date they will discuss with Dealbid is the UTC one.
 *
 * These match `dealbid-web`'s formatters value-for-value; the seller sees their own deal
 * written the same way a buyer sees it.
 */

const CENTS_PER_UNIT = 100;

/**
 * USD, with cents shown only when there are any: `$20`, `$19.50`, `$0`.
 *
 * A promotional credit balance is usually whole dollars, and a column of `.00` reads as
 * noise; the amounts that do carry cents keep them.
 */
export function formatMoney(cents: number): string {
  const hasFraction = cents % CENTS_PER_UNIT !== 0;
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    minimumFractionDigits: hasFraction ? 2 : 0,
    maximumFractionDigits: hasFraction ? 2 : 0,
  }).format(cents / CENTS_PER_UNIT);
}

/** `72% off`. Core derives the percentage; this only words it. */
export function formatPercentOff(percent: number): string {
  return `${String(percent)}% off`;
}

const UTC_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * `24 Aug 2026` from an ISO-8601 instant, in UTC.
 *
 * Returns `null` for anything unparseable rather than printing `Invalid Date`: a review
 * timestamp that did not survive the wire is better omitted than shown as a broken value.
 */
export function formatInstantDate(iso: string | null | undefined): string | null {
  if (typeof iso !== "string") return null;
  const parsed = Date.parse(iso);
  return Number.isNaN(parsed) ? null : UTC_DATE.format(new Date(parsed));
}

/** Turns a `YYYY-MM-DD` display date into a `Date` at UTC midnight. */
function parseDisplayDate(displayDate: string): Date | null {
  const parsed = Date.parse(`${displayDate}T00:00:00.000Z`);
  return Number.isNaN(parsed) ? null : new Date(parsed);
}

const LONG_DATE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "long",
  day: "numeric",
  month: "long",
  year: "numeric",
});

/**
 * `Tuesday 25 August 2026` from a `YYYY-MM-DD` display date, in UTC.
 *
 * Used in prose, where the day a placement runs has to be unambiguous. An unparseable value
 * is returned as core sent it rather than rendered as `Invalid Date`: the raw date is still
 * the one a founder would quote to Dealbid.
 */
export function formatDisplayDateLong(displayDate: string): string {
  const date = parseDisplayDate(displayDate);
  return date === null ? displayDate : LONG_DATE.format(date).replace(",", "");
}

/** `25 Aug 2026`. Used where the date labels a board rather than sits in a sentence. */
export function formatDisplayDateShort(displayDate: string): string {
  const date = parseDisplayDate(displayDate);
  return date === null ? displayDate : UTC_DATE.format(date);
}

const TODAY_LINE = new Intl.DateTimeFormat("en-GB", {
  timeZone: "UTC",
  weekday: "short",
  day: "numeric",
  month: "short",
  year: "numeric",
});

/**
 * `Fri 29 Aug 2026` — today, in UTC (`APP-016`).
 *
 * The desk's masthead states the date it is working in and that every date on it is UTC.
 * Canon § 8 puts the day boundary at 00:00 UTC and every date this app prints is already in
 * that zone; before this line existed the founder had no way to know that from a bare
 * `28 Aug 2026` on a deal card.
 *
 * Takes the instant rather than reading the clock, so a caller decides when it is sampled.
 */
export function formatTodayUtc(nowMs: number): string {
  return TODAY_LINE.format(new Date(nowMs)).replace(",", "");
}
